import React from 'react';
import axios from 'axios';
import { Alert, Autocomplete, Box, Button, CircularProgress, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PdfToPptUpload from 'components/pdf-to-ppt-upload';
import { AmaV2Client } from 'context/ama-v2-provider/ama-v2.types';
import { useAmaV2Context } from 'context/ama-v2-provider';
import { useAuth } from 'context/auth-provider';

interface UploadUrlResponse {
  jobId?: string;
  job_id?: string;
  upload_url?: string;
  upload_fields?: Record<string, string>;
}

const REQUIRED_UPLOAD_FIELDS = [
  'key',
  'policy',
  'x-amz-algorithm',
  'x-amz-credential',
  'x-amz-date',
  'x-amz-signature',
  'x-amz-security-token',
] as const;

interface ClientOption extends AmaV2Client {
  label: string;
}

interface SnapshotDateOption {
  snapshotDate: string;
  isActive: boolean;
  label: string;
}

const mapClientsXtd = (clients: AmaV2Client[]): ClientOption[] => {
  const activeClients = clients.filter((client) => client.is_active === true);
  return activeClients
    .map((client) => ({
      ...client,
      label: client.client_id,
    }))
    .sort((a, b) => a.client_id.localeCompare(b.client_id));
};

export default function DashboardProcessorView() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { clientsState, loadClients } = useAmaV2Context();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [selectedClient, setSelectedClient] = React.useState<ClientOption | null>(null);
  const [selectedSnapshotDate, setSelectedSnapshotDate] = React.useState<SnapshotDateOption | null>(null);
  const [snapshotDatesByClient, setSnapshotDatesByClient] = React.useState<Record<string, SnapshotDateOption[]>>({});
  const snapshotDatesByClientRef = React.useRef<Record<string, SnapshotDateOption[]>>({});
  const [snapshotDatesLoading, setSnapshotDatesLoading] = React.useState<boolean>(false);
  const [snapshotDatesError, setSnapshotDatesError] = React.useState<string | null>(null);
  const [formResetKey, setFormResetKey] = React.useState<number>(0);
  const [isConverting, setIsConverting] = React.useState<boolean>(false);
  const [alertState, setAlertState] = React.useState<{ severity: 'error' | 'success' | 'info'; message: string; jobId?: string; clientId?: string } | null>(null);

  const reportType = 'snapshot_descriptive';
  const clientOptions = React.useMemo(() => mapClientsXtd(clientsState.data), [clientsState.data]);
  const snapshotDateOptions = React.useMemo(() => {
    if (!selectedClient) return [];
    return snapshotDatesByClient[selectedClient.client_id] ?? [];
  }, [selectedClient, snapshotDatesByClient]);

  const selectedSnapshotDateValue = selectedSnapshotDate?.snapshotDate ?? null;

  React.useEffect(() => {
    if (!user) return;
    if (clientsState.status === 'idle') {
      loadClients();
    }
  }, [clientsState.status, loadClients, user]);

  const axiosInstance = React.useMemo(() => {
    if (!user) return null;
    return axios.create({
      baseURL: process.env.REACT_APP_AMA_V2_API_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: user.idToken,
      },
    });
  }, [user]);

  React.useEffect(() => {
    snapshotDatesByClientRef.current = snapshotDatesByClient;
  }, [snapshotDatesByClient]);

  const fetchSnapshotDatesForClient = React.useCallback(async (clientId: string): Promise<SnapshotDateOption[]> => {
    if (!axiosInstance || !user) return [];

    const cachedDates = snapshotDatesByClientRef.current[clientId];
    if (cachedDates) {
      return cachedDates;
    }

    const currentClient = clientsState.data.find((client) => client.client_id === clientId) ?? null;
    const activeSnapshot = currentClient?.active_snapshot ?? null;

    setSnapshotDatesLoading(true);
    setSnapshotDatesError(null);

    try {
      const response = await axiosInstance.get('/snapshots', { params: { clientId } });
      const items = Array.isArray(response.data?.items)
        ? response.data.items
        : Array.isArray(response.data)
          ? response.data
          : [];

      const mappedOptions: SnapshotDateOption[] = [];
      const seenSnapshotDates = new Set<string>();

      for (const snapshot of items as Array<Record<string, any>>) {
        const snapshotDate = snapshot.snapshot_date ?? snapshot.snapshotDate;
        const snapshotAcademicYear = snapshot.academic_year ?? snapshot.academicYear;
        if (typeof snapshotDate !== 'string' || snapshotDate.length === 0 || seenSnapshotDates.has(snapshotDate)) {
          continue;
        }

        seenSnapshotDates.add(snapshotDate);
        const isActive = Boolean(
          activeSnapshot &&
          snapshotAcademicYear &&
          activeSnapshot.academic_year === snapshotAcademicYear &&
          activeSnapshot.snapshot_date === snapshotDate
        );
        mappedOptions.push({
          snapshotDate,
          isActive,
          label: `${snapshotDate}${isActive ? ' Active' : ''}`,
        });
      }

      const sortedDates = [...mappedOptions].sort((a, b) => b.snapshotDate.localeCompare(a.snapshotDate));
      setSnapshotDatesByClient((prev) => {
        if (prev[clientId]) {
          return prev;
        }
        return { ...prev, [clientId]: sortedDates };
      });
      return sortedDates;
    } catch (err) {
      console.error('DashboardProcessorView.fetchSnapshotDatesForClient :: error', err);
      setSnapshotDatesError('Failed to load snapshot dates for this client.');
      return [];
    } finally {
      setSnapshotDatesLoading(false);
    }
  }, [axiosInstance, clientsState.data, user]);

  const handleClientChange = React.useCallback((client: ClientOption | null) => {
    setSelectedClient(client);
    setSelectedSnapshotDate(null);

    if (client) {
      void fetchSnapshotDatesForClient(client.client_id);
    }
  }, [fetchSnapshotDatesForClient]);

  const getUsername = React.useCallback((): string => {
    const email = user?.email || '';
    return email.includes('@') ? email.split('@')[0] : email;
  }, [user]);

  const handleFileSelected = React.useCallback((file: File | null) => {
    setSelectedFile(file);
    setAlertState(null);
    if (file) {
      console.log('DashboardProcessorView.handleFileSelected :: file collected', {
        name: file.name,
        size: file.size,
        type: file.type,
      });
    }
  }, []);

  const resetForm = React.useCallback(() => {
    setSelectedFile(null);
    setSelectedClient(null);
    setSelectedSnapshotDate(null);
    setFormResetKey((prev) => prev + 1);
  }, []);

  const handleConvertClick = React.useCallback(async () => {
    let currentStage = 'initialization';

    if (!user || !axiosInstance) {
      setAlertState({ severity: 'error', message: 'User not authenticated' });
      return;
    }

    if (!selectedFile) {
      setAlertState({ severity: 'error', message: 'Please select a PDF file before converting.' });
      return;
    }

    if (!selectedClient) {
      setAlertState({ severity: 'error', message: 'Please select a client before converting.' });
      return;
    }

    if (!selectedSnapshotDate) {
      setAlertState({ severity: 'error', message: 'Please select a snapshot date before converting.' });
      return;
    }

    setIsConverting(true);
    setAlertState({ severity: 'info', message: 'Step 1/3: Requesting upload URL...' });

    try {
      currentStage = 'get-upload-url';
      const uploadUrlResponse = await axiosInstance.post<UploadUrlResponse>('/get-upload-url', {});
      const uploadUrl = uploadUrlResponse.data?.upload_url || '';
      const uploadFields = uploadUrlResponse.data?.upload_fields || {};
      const jobId = uploadUrlResponse.data?.jobId || uploadUrlResponse.data?.job_id || '';

      if (!uploadUrl || !jobId) {
        throw new Error('Upload URL response is missing required fields.');
      }

      const missingField = REQUIRED_UPLOAD_FIELDS.find((field) => !uploadFields[field]);
      if (missingField) {
        throw new Error(`Upload URL response is missing required upload field: ${missingField}`);
      }

      const formData = new FormData();
      formData.append('key', uploadFields.key);
      formData.append('Content-Type', uploadFields['Content-Type'] || selectedFile.type || 'application/pdf');
      formData.append('policy', uploadFields.policy);
      formData.append('x-amz-algorithm', uploadFields['x-amz-algorithm']);
      formData.append('x-amz-credential', uploadFields['x-amz-credential']);
      formData.append('x-amz-date', uploadFields['x-amz-date']);
      formData.append('x-amz-signature', uploadFields['x-amz-signature']);
      formData.append('x-amz-security-token', uploadFields['x-amz-security-token']);
      formData.append('file', selectedFile, selectedFile.name);

      console.log('DashboardProcessorView.handleConvertClick :: upload fields prepared', {
        jobId,
        uploadUrl,
        key: uploadFields.key,
        contentType: uploadFields['Content-Type'] || selectedFile.type || 'application/pdf',
      });

      currentStage = 's3-upload';
      setAlertState({ severity: 'info', message: 'Uploading PDF...' });
      const uploadResponse = await axios.post(uploadUrl, formData);

      console.log('DashboardProcessorView.handleConvertClick :: upload complete', {
        jobId,  
        status: uploadResponse.status,
      });

      currentStage = 'pdf-to-ppt-conversion';
      setAlertState({ severity: 'info', message: 'Submitting conversion job...' });
      const conversionResponse = await axiosInstance.post('/pdf-to-ppt-conversion', {
        username: getUsername(),
        report_type: reportType,
        jobId,
        client_id: selectedClient.client_id,
        snapshot_date: selectedSnapshotDate.snapshotDate,
      });

      console.log('DashboardProcessorView.handleConvertClick :: conversion submitted', conversionResponse.data);
      const successClientId = selectedClient.client_id;
      setAlertState({
        severity: 'success',
        message: 'PPT conversion is in progress.',
        jobId,
        clientId: successClientId,
      });
      resetForm();
    } catch (err: unknown) {
      console.error('DashboardProcessorView.handleConvertClick :: error', err);

      const stagePrefix =
        currentStage === 'get-upload-url'
          ? 'Failed at Step 1/3 (get-upload-url): '
          : currentStage === 's3-upload'
            ? 'Failed at Step 2/3 (S3 upload): '
            : currentStage === 'pdf-to-ppt-conversion'
              ? 'Failed at Step 3/3 (pdf-to-ppt-conversion): '
              : 'Conversion failed: ';

      if (axios.isAxiosError(err)) {
        const responseData = err.response?.data;
        const stringBody = typeof responseData === 'string' ? responseData : '';
        const responseMessageFromObject = typeof responseData?.message === 'string' ? responseData.message : '';
        const message =
          responseMessageFromObject ||
          stringBody ||
          err.message;
        setAlertState({ severity: 'error', message: `Some error occurred: ${message}` });
      } else {
        const fallback = err instanceof Error ? err.message : 'Failed to submit PDF to PPT conversion.';
        setAlertState({ severity: 'error', message: `Some error occurred: ${fallback}` });
      }
    } finally {
      setIsConverting(false);
    }
  }, [axiosInstance, getUsername, reportType, selectedClient, selectedFile, user]);

  return (
    <Box sx={{ height: '100%', minHeight: 0, position: 'relative' }}>
      <Stack spacing={3} sx={{ height: '100%', overflowY: 'auto', pr: 1, pb: 10 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
            Dashboard Processor
          </Typography>
          <Box component="section" sx={{ color: 'text.secondary' }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              1. This tool converts snapshot descriptive PDF files into PowerPoint presentations.
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              2. Upload a PDF file to begin the handoff workflow.
            </Typography>
            <Typography variant="body1">
              3. After processing completes, the generated PowerPoint presentation can be downloaded from the Jobs section.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
            Client List
          </Typography>

          <Autocomplete
            disablePortal
            id="dashboard-processor-client-id"
            options={clientOptions}
            sx={{ width: '100%' }}
            value={selectedClient}
            onChange={(_event, client) => handleClientChange(client)}
            isOptionEqualToValue={(option, value) => option.client_id === value.client_id}
            loading={clientsState.status === 'loading'}
            noOptionsText={clientsState.status === 'loading' ? 'Loading clients...' : 'No active clients found.'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client ID"
                type="text"
                helperText={selectedClient ? `Selected client: ${selectedClient.client_id}` : 'Select a client from the active client list.'}
              />
            )}
          />

          <Autocomplete
            disablePortal
            id="dashboard-processor-snapshot-date"
            options={snapshotDateOptions}
            sx={{ width: '100%', mt: 2 }}
            value={selectedSnapshotDate}
            onChange={(_event, snapshotDate) => setSelectedSnapshotDate(snapshotDate ?? null)}
            disabled={!selectedClient || snapshotDateOptions.length === 0 || snapshotDatesLoading}
            loading={snapshotDatesLoading}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.snapshotDate)}
            isOptionEqualToValue={(option, value) => option.snapshotDate === value.snapshotDate}
            noOptionsText={
              !selectedClient
                ? 'Select a client to load snapshot dates.'
                : snapshotDatesLoading
                  ? 'Loading snapshot dates...'
                  : 'No snapshot dates found for this client.'
            }
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                <Typography component="span" variant="body2">{option.snapshotDate}</Typography>
                {option.isActive && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.75,
                      py: 0.2,
                      borderRadius: 1,
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    Active
                  </Box>
                )}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Snapshot Date"
                type="text"
                helperText={
                  !selectedClient
                    ? 'Select a client to populate available snapshot dates.'
                    : snapshotDatesError
                      ? snapshotDatesError
                      : snapshotDateOptions.length === 0
                        ? 'No snapshots are available for the selected client.'
                        : 'Select the snapshot date to convert.'
                }
              />
            )}
          />

          {clientsState.status === 'loading' && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={18} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Loading clients...
              </Typography>
            </Box>
          )}

          {clientsState.status === 'error' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to load clients.{' '}
              <Link
                component="button"
                type="button"
                underline="always"
                color="inherit"
                onClick={() => loadClients()}
              >
                Retry
              </Link>
            </Alert>
          )}

          {clientsState.status === 'success' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Active clients: {clientOptions.length}
            </Typography>
          )}
        </Box>

        {alertState && (
          <Alert severity={alertState.severity}>
            {alertState.severity === 'success' && alertState.clientId ? (
              <Typography component="span" sx={{ fontWeight: 700 }}>
                {alertState.message}{' '}
                <Link
                  component="button"
                  type="button"
                  underline="always"
                  color="primary"
                  onClick={() => navigate('/ama-v2/file-explorer', { state: { clientId: alertState.clientId } })}
                >
                  Click here
                </Link>
                {' '}to open the client folder and monitor the generated PPT.
              
              </Typography>
            ) : (
              alertState.message
            )}
          </Alert>
        )}

        <PdfToPptUpload
          key={formResetKey}
          title="Upload Snapshot Descriptive PDF"
          helperText="Drag and drop a PDF file here, or click the button to select one from your device."
          onFileSelected={handleFileSelected}
        />

      </Stack>

      <Box sx={{ position: 'absolute', bottom: 10, right: 16, zIndex: 2 }}>
        <Button
          variant="contained"
          disabled={!selectedFile || !selectedClient || !selectedSnapshotDateValue || isConverting}
          onClick={handleConvertClick}
          sx={{ minWidth: 180 }}
        >
          {isConverting ? <CircularProgress size={20} color="inherit" /> : 'Convert To PPT'}
        </Button>
      </Box>
    </Box>
  );
}